import { useSession } from '@/providers/session-provider';
import { Urls } from '@/lib/urls';
import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  failedInvoicesCountQueryOptions,
  useCanViewFailedInvoices,
} from '@/app/dashboard/invoicing/failed-deliveries/utils';
import { fetchNonAttestedDebtCount } from '@/app/dashboard/invoicing/invoices/hooks/use-non-attested-debt-custom-filter';
import { usePerms } from '@/hooks/usePerms';
import { SPECIAL_INVALIDATION_KEYS } from '@pigello/construct-api';
import { useReadGlobalFilter } from '@/lib/global-filter/hooks/use-global-filter';
import type { GlobalFilter } from '@/lib/global-filter/types';

type CountResponse = { count: number; identifier?: string };

interface CountQueryContext {
  organizationId?: string;
  globalFilter: GlobalFilter[string];
  canViewFailedInvoices: boolean;
  canViewDebtInvoices: boolean;
}

/**
 * A page whose list is constrained by its own base filters (not the URL
 * filter a bookmark stores) registers its count here, so workflow bookmarks
 * pointing at it show the same number as the page.
 */
interface CustomCountEntry {
  canView: (context: CountQueryContext) => boolean;
  queryOptions: (context: CountQueryContext) => UseQueryOptions<CountResponse>;
}

const customCountQueryRegistry: Record<string, CustomCountEntry> = {
  [Urls.dashboard.invoicing.failedDelivieries]: {
    canView: ({ canViewFailedInvoices }) => canViewFailedInvoices,
    queryOptions: ({ organizationId, canViewFailedInvoices }) =>
      failedInvoicesCountQueryOptions({
        organizationId,
        countOnly: true,
        enabled: canViewFailedInvoices,
      }),
  },
  [Urls.dashboard.invoicing.nonAttestedDebthandling]: {
    canView: ({ canViewDebtInvoices }) => canViewDebtInvoices,
    queryOptions: ({ globalFilter, canViewDebtInvoices }) => ({
      // Under the content type's multi prefix, so invalidateConstructQueries
      // for accounting.debtinvoice refreshes it.
      queryKey: [
        'accounting.debtinvoice',
        SPECIAL_INVALIDATION_KEYS.multi,
        'non-attested-bookmark-count',
        globalFilter,
      ],
      queryFn: async () => ({ count: await fetchNonAttestedDebtCount() }),
      enabled: canViewDebtInvoices,
    }),
  },
};

export function useCustomCountByPath(path: string):
  | (Omit<UseQueryResult<CountResponse, Error>, 'data'> & {
      data: CountResponse | undefined;
    })
  | undefined {
  const {
    data: {
      session: { organizationId },
    },
  } = useSession();
  const canViewFailedInvoices = useCanViewFailedInvoices();
  const { canView: canViewDebtInvoices } = usePerms('debtinvoice');
  const globalFilter = useReadGlobalFilter();

  const entry = customCountQueryRegistry[path];
  const context: CountQueryContext = {
    organizationId,
    globalFilter,
    canViewFailedInvoices,
    canViewDebtInvoices,
  };

  const queryOptions: UseQueryOptions<CountResponse> = entry
    ? entry.queryOptions(context)
    : {
        queryKey: ['empty', path],
        queryFn: async () => ({ count: 0 }),
        enabled: false,
      };

  const result = useQuery(queryOptions);

  if (!entry) return undefined;

  const canView = entry.canView(context);
  return {
    ...result,
    data: canView && !result.isError ? result.data : undefined,
    isLoading: canView && result.isLoading,
  };
}
